/* eslint-disable @atlaskit/design-system/ensure-design-token-usage */
/* eslint-disable @atlaskit/ui-styling-standard/enforce-style-prop */
/* eslint-disable @atlaskit/ui-styling-standard/no-imported-style-values */
/* eslint-disable @atlaskit/ui-styling-standard/no-unsafe-values */
/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { cssMap, jsx } from '@compiled/react';
import { motion, useMotionValueEvent, useScroll, useSpring, useTransform } from 'motion/react';

import { getDocument } from '@atlaskit/browser-apis';

import { useSectionReveal } from '../../../../common/analytics/hooks/useSectionReveal';
import { SectionName } from '../../../../common/analytics/types';
import { NAV_BAR_HEIGHT_PX } from '../../../components/NavBar';
import { getNearestScrollContainer } from '../../../utils/scroll-container';

import { BackgroundIcons } from './BackgroundIcons';
import { ContentStack } from './ContentStack';
import { getHeroLayout } from './layout';
import { usePointerInteractivity } from './use-pointer-interactivity';
import { usePrefersReducedMotion } from './use-prefers-reduced-motion';
import { useViewportTier } from './use-viewport-tier';

const EDGE_CONFIG = {
	density: 3,
	radius: 600,
	opacity: 0.25,
} as const;

const LAYOUT_CONFIG = {
	ovalSize: 0.82,
	ovalAspectRatio: 1.19,
	mobileOvalSize: 1.16,
	mobileOvalAspectRatio: 0.85,
} as const;

const AMBIENT_CONFIG = {
	ambientAmp: 2.5,
} as const;

const PHYSICS_CONFIG = {
	kSelf: 80,
	kEdge: 20,
	damping: 8,
	flick: 0.5,
} as const;

const INTRO_CONFIG = {
	avatarStaggerMs: 60,
	edgeGrowMs: 800,
	iconAppearMs: 300,
	iconAppearOvershoot: 1.8,
} as const;

const SHIMMER_CONFIG = {
	enabled: true,
	startOffsetMs: -2000,
	avgIntervalMs: 120,
	maxConcurrent: 10,
	durationMs: 1420,
	pulseLen: 0.37,
	brightness: 0.5,
	strokeWidth: 1.5,
	glow: 0.3,
} as const;

const HERO_MOTION_FREEZE_SCROLL_PROGRESS = 0.04;
const HERO_MOTION_UNFREEZE_SCROLL_PROGRESS = 0.025;

const TYPOGRAPHY_HERO_CONFIG = {
	scale: 1,
} as const;

const TYPOGRAPHY_LETTER_A_CONFIG = {
	lineWidth: 3,
	durationMs: 800,
	startTimeMs: -1970,
	nodePhaseStart: 0.1,
	nodePhaseEnd: 1,
	shuffleNodes: false,
	shuffleSeed: 300,
} as const;

const TYPOGRAPHY_SCRIBBLE_CONFIG = {
	durationMs: 1000,
	startTimeMs: 700,
	ease: 'ease-out',
	lineWidth: 10.5,
	blurPx: 0.5,
} as const;

const TYPOGRAPHY_LETTER_INTRO_CONFIG = {
	style: 'fade-in',
	fadeDurationMs: 400,
	blurStartPx: 8,
	totalDurationMs: 450,
	startDelayMs: 480,
	shuffleSeed: 532,
} as const;

const CTA_TIMING_CONFIG = {
	subtitleStartMs: 1000,
	subtitleFadeMs: 400,
	ctaStartMs: 1150,
	ctaFadeMs: 500,
} as const;

const TYPOGRAPHY_MOUNT_KEY = 'twg-hero-defaults';
const HERO_ASSET_READY_TIMEOUT_MS = 1600;

function initialIntroDone(): boolean {
	const doc = getDocument();
	if (!doc) return false;
	return doc.visibilityState !== 'visible';
}

function waitForHeroImageDecode(src: string): Promise<void> {
	return new Promise((resolve) => {
		if (typeof Image === 'undefined') {
			resolve();
			return;
		}

		const image = new Image();
		let settled = false;
		const finish = () => {
			if (settled) {
				return;
			}
			settled = true;
			resolve();
		};

		image.onload = () => {
			const decode = image.decode?.();
			if (decode) {
				decode.then(finish, finish);
				return;
			}
			finish();
		};
		image.onerror = finish;
		image.src = src;

		if (image.complete) {
			const decode = image.decode?.();
			if (decode) {
				decode.then(finish, finish);
				return;
			}
			finish();
		}
	});
}

// Some production runtimes do not implement Promise.allSettled, which caused
// HeroIcons to crash during image preload (https://atlassian-2y.sentry.io/issues/7464332880/?query=is:unresolved%20issue.category:%5Berror,outage%5D)
// Keep this helper so hero asset readiness waits for all promises without requiring allSettled.
function waitForAllPromisesToSettle(promises: Array<Promise<void>>): Promise<void> {
	if (typeof Promise.allSettled === 'function') {
		return Promise.allSettled(promises).then(() => undefined);
	}

	return Promise.all(
		promises.map((promise) =>
			promise.then(
				() => undefined,
				() => undefined,
			),
		),
	).then(() => undefined);
}

const styles = cssMap({
	stageWrapper: {
		position: 'relative',
		width: '100%',
		height: '1180px',
		backgroundColor: '#111213',
	},
	stageSticky: {
		position: 'sticky',
		top: 0,
		width: '100%',
		height: '1000px',
		overflow: 'hidden',
		backgroundColor: '#111213',
	},
	artboard: {
		position: 'absolute',
		top: '50%',
		left: '50%',
		width: '1680px',
		height: '1200px',
		transform: 'translate(-50%, -50%)',
	},
	heroIconLayer: {
		position: 'absolute',
		inset: 0,
		willChange: 'transform, opacity, filter',
	},
	heroContentLayer: {
		position: 'absolute',
		inset: 0,
		zIndex: 2,
		willChange: 'transform, opacity',
	},
	mobileStageWrapper: {
		height: `calc((100dvh - ${NAV_BAR_HEIGHT_PX}px) * 1.18)`,
	},
	mobileStageSticky: {
		height: `calc(100dvh - ${NAV_BAR_HEIGHT_PX}px)`,
	},
	mobileArtboard: {
		width: '650px',
		height: `calc(100dvh - ${NAV_BAR_HEIGHT_PX}px)`,
	},
});

export type HeroIconsProps = {
	/**
	 * Optional content rendered inside the centered hero content layer.
	 * When omitted, the default marketing `ContentStack` (animated wordmark,
	 * subtitle, and CTA) is rendered. Pass a custom node to reuse the
	 * surrounding-icon animation with different center content (for example,
	 * an error state).
	 */
	centerContent?: React.ReactNode;
};

export function HeroIcons({ centerContent }: HeroIconsProps = {}): JSX.Element {
	const stageRef = useRef<HTMLDivElement | null>(null);
	const scrollSceneRef = useRef<HTMLDivElement | null>(null);
	const scrollContainerRef = useRef<HTMLElement | null>(null);
	const sectionRef = useSectionReveal<HTMLDivElement>(SectionName.Hero);
	const viewportTier = useViewportTier();
	const iconsInteractive = usePointerInteractivity();
	const reducedMotion = usePrefersReducedMotion();
	const [introDone, setIntroDone] = useState<boolean>(initialIntroDone);
	const [heroAssetsReady, setHeroAssetsReady] = useState<boolean>(initialIntroDone);
	const [motionFrozen, setMotionFrozen] = useState(false);
	const [scrollContainer, setScrollContainer] = useState<HTMLElement | null>(null);
	const motionFrozenRef = useRef(false);
	const replayHandleRef = useRef<(() => void) | null>(null);
	const isMobileLayout = viewportTier === 'mobile';
	const heroAssetSources = useMemo(
		() =>
			Array.from(
				new Set(
					getHeroLayout({ viewportTier, ...LAYOUT_CONFIG })
						.assets.map((asset) => asset.src)
						.filter((src): src is string => Boolean(src)),
				),
			),
		[viewportTier],
	);
	const { scrollYProgress } = useScroll({
		target: scrollSceneRef,
		offset: ['start start', 'end start'],
		...(scrollContainer ? { container: scrollContainerRef } : {}),
	});
	const smoothScrollProgress = useSpring(scrollYProgress, {
		stiffness: 120,
		damping: 32,
		mass: 0.2,
	});
	const iconScale = useTransform(smoothScrollProgress, [0, 1], [1, 1.08]);
	const iconOpacity = useTransform(smoothScrollProgress, [0, 0.18, 0.82], [1, 0.8, 0.52]);
	const iconBlur = useTransform(
		smoothScrollProgress,
		[0, 0.08, 0.55],
		['blur(0px)', 'blur(3px)', 'blur(8px)'],
	);
	const contentY = useTransform(smoothScrollProgress, [0, 0.9], ['0%', '-14%']);
	const contentScale = useTransform(smoothScrollProgress, [0, 1], [1, 1.04]);
	const contentOpacity = useTransform(smoothScrollProgress, [0, 0.82, 1], [1, 1, 0]);

	useMotionValueEvent(scrollYProgress, 'change', (latest) => {
		if (reducedMotion) {
			return;
		}

		const nextMotionFrozen = motionFrozenRef.current
			? latest >= HERO_MOTION_UNFREEZE_SCROLL_PROGRESS
			: latest >= HERO_MOTION_FREEZE_SCROLL_PROGRESS;

		if (nextMotionFrozen === motionFrozenRef.current) {
			return;
		}

		motionFrozenRef.current = nextMotionFrozen;
		setMotionFrozen(nextMotionFrozen);
	});

	useEffect(() => {
		const doc = getDocument();
		const win = doc?.defaultView;
		if (!win || heroAssetSources.length === 0) {
			setHeroAssetsReady(true);
			return;
		}

		let cancelled = false;
		setHeroAssetsReady(false);
		const timeoutId = win.setTimeout(() => {
			if (!cancelled) {
				setHeroAssetsReady(true);
			}
		}, HERO_ASSET_READY_TIMEOUT_MS);

		waitForAllPromisesToSettle(heroAssetSources.map((src) => waitForHeroImageDecode(src))).then(
			() => {
				if (cancelled) {
					return;
				}
				win.clearTimeout(timeoutId);
				setHeroAssetsReady(true);
			},
		);

		return () => {
			cancelled = true;
			win.clearTimeout(timeoutId);
		};
	}, [heroAssetSources]);

	const onIntroComplete = useCallback(() => {
		setIntroDone(true);
	}, []);
	// Hold the latest `sectionRef` in a ref so the merged ref callback below
	// can keep a stable identity for the lifetime of the component. Without
	// this, any change in `sectionRef`'s identity (e.g. from an upstream
	// analytics context re-render that recreates `fireEvent` →
	// `useSectionReveal`'s callback) would cause React to run the
	// detach-with-null + attach-with-node cycle on every render. Each cycle
	// calls `setScrollContainer` with alternating values, which trips React's
	// "Maximum update depth exceeded" guard during commit (TWG-MICROSITE-1N).
	const sectionRefLatest = useRef(sectionRef);
	sectionRefLatest.current = sectionRef;
	const setScrollSceneNode = useCallback((node: HTMLDivElement | null) => {
		scrollSceneRef.current = node;
		const nextScrollContainer = getNearestScrollContainer(node);
		scrollContainerRef.current = nextScrollContainer;
		setScrollContainer((currentScrollContainer) =>
			currentScrollContainer === nextScrollContainer ? currentScrollContainer : nextScrollContainer,
		);
		sectionRefLatest.current(node);
	}, []);

	const effectiveAmbient = reducedMotion ? { ...AMBIENT_CONFIG, ambientAmp: 0 } : AMBIENT_CONFIG;
	const effectiveIntro = reducedMotion
		? {
				...INTRO_CONFIG,
				avatarStaggerMs: 0,
				edgeGrowMs: 0,
				iconAppearMs: 0,
				iconAppearOvershoot: 1,
			}
		: INTRO_CONFIG;
	const effectiveShimmer = reducedMotion
		? { ...SHIMMER_CONFIG, enabled: false, maxConcurrent: 1, glow: 0 }
		: SHIMMER_CONFIG;

	return (
		<div
			ref={setScrollSceneNode}
			css={[styles.stageWrapper, isMobileLayout && styles.mobileStageWrapper]}
		>
			<div css={[styles.stageSticky, isMobileLayout && styles.mobileStageSticky]}>
				<div ref={stageRef} css={[styles.artboard, isMobileLayout && styles.mobileArtboard]}>
					<motion.div
						css={styles.heroIconLayer}
						style={
							reducedMotion
								? undefined
								: { scale: iconScale, opacity: iconOpacity, filter: iconBlur }
						}
					>
						{heroAssetsReady && (
							<BackgroundIcons
								stageRef={stageRef}
								introDone={introDone}
								onIntroComplete={onIntroComplete}
								replayHandleRef={replayHandleRef}
								edges={EDGE_CONFIG}
								layout={LAYOUT_CONFIG}
								ambient={effectiveAmbient}
								physics={PHYSICS_CONFIG}
								intro={effectiveIntro}
								shimmer={effectiveShimmer}
								viewportTier={viewportTier}
								interactive={iconsInteractive}
								motionFrozen={motionFrozen}
							/>
						)}
					</motion.div>
					<motion.div
						css={styles.heroContentLayer}
						style={
							reducedMotion
								? undefined
								: {
										y: contentY,
										scale: contentScale,
										opacity: contentOpacity,
									}
						}
					>
						{centerContent ?? (
							<ContentStack
								mountKey={TYPOGRAPHY_MOUNT_KEY}
								viewportTier={viewportTier}
								reducedMotion={reducedMotion}
								hero={TYPOGRAPHY_HERO_CONFIG}
								letterA={TYPOGRAPHY_LETTER_A_CONFIG}
								scribble={TYPOGRAPHY_SCRIBBLE_CONFIG}
								letterIntro={TYPOGRAPHY_LETTER_INTRO_CONFIG}
								cta={CTA_TIMING_CONFIG}
							/>
						)}
					</motion.div>
				</div>
			</div>
		</div>
	);
}
